/**
 * JSX-free source of truth for blog post metadata.
 * Lives apart from blogPosts.jsx so it can be imported from plain Node
 * (build-time prerender in scripts/prerender.mjs) and from src/seo/routeMeta.js
 * without pulling in the JSX article bodies. blogPosts.jsx spreads these in.
 */
export const blogPostsMeta = [
  {
    slug: 'security-deposits-in-dubai-complete-guide',
    title: 'The Complete Guide to Security Deposits in Dubai (2026)',
    description:
      'Everything tenants, renters, and businesses need to know about the security deposit in Dubai — how it works, common disputes, and how to get yours back faster.',
    category: 'Guides',
    date: '2026-06-16',
    dateLabel: 'June 16, 2026',
    readingTime: '8 min read',
  },
  {
    slug: 'rent-a-car-without-security-deposit-dubai',
    title: 'How to Rent a Car Without a Security Deposit in Dubai',
    description:
      "Want to rent a car without a security deposit in Dubai? Here's how deposit-free car rental works, what to watch for, and how businesses offer it without losing protection.",
    category: 'Car Rental',
    date: '2026-06-25',
    dateLabel: 'June 25, 2026',
    readingTime: '7 min read',
  },
  {
    slug: 'traditional-vs-digital-deposit-collection',
    title: "Traditional vs. Digital Security Deposit Collection: What's Changed",
    description:
      'Cash, cheques, and card blocks vs. digital authorizations. Compare the old and new ways to collect a security deposit — and why UAE businesses are switching.',
    category: 'Comparisons',
    date: '2026-07-06',
    dateLabel: 'July 6, 2026',
    readingTime: '6 min read',
  },
  {
    slug: 'how-businesses-use-security-deposits-in-operations',
    title: 'How Businesses Use Security Deposits in Day-to-Day Operations',
    description:
      'From car rentals to holiday homes, here is how a security deposit fits into daily operations across rental industries — and why it can become part of your marketing strategy, not just your risk policy.',
    category: 'Business',
    date: '2026-07-16',
    dateLabel: 'July 16, 2026',
    readingTime: '7 min read',
  },
  {
    slug: 'security-deposit-law-uae',
    title: 'Security Deposit Law in the UAE: What Tenants and Landlords Need to Know',
    seoTitle: 'Security Deposit Law in the UAE',
    description:
      'What UAE security deposit law actually says — Article 20 of Dubai Law 26 of 2007, refund rights, legal deductions, and what to do when a deposit is withheld.',
    category: 'Tenancy',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '7 min read',
  },
  {
    slug: 'rera-security-deposit-rules-dubai',
    title: 'RERA Security Deposit Rules in Dubai, Explained',
    description:
      "RERA's rules for security deposits in Dubai: how much landlords can take, what they can deduct, and how RERA and the Rental Dispute Centre handle deposit disputes.",
    category: 'Tenancy',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '6 min read',
  },
  {
    slug: 'ejari-security-deposit',
    title: 'Ejari and Your Security Deposit: Why Registration Matters',
    description:
      'Ejari registration is what makes your security deposit enforceable in Dubai. What Ejari records, why unregistered contracts fail at the Rental Dispute Centre, and how to protect your deposit.',
    category: 'Tenancy',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '6 min read',
  },
  {
    slug: 'how-to-get-security-deposit-back-dubai',
    title: 'How to Get Your Security Deposit Back in Dubai: A Step-by-Step Guide',
    seoTitle: 'How to Get Your Security Deposit Back in Dubai',
    description:
      'A practical, step-by-step guide to getting your security deposit back in Dubai — documentation, the handover inspection, written demands, and escalating to the Rental Dispute Centre.',
    category: 'Tenancy',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '8 min read',
  },
  {
    slug: 'landlord-not-returning-security-deposit-dubai',
    title: "Landlord Not Returning Your Security Deposit in Dubai? Here's What to Do",
    seoTitle: 'Landlord Not Returning Your Deposit in Dubai?',
    description:
      'If your landlord is not returning your security deposit in Dubai, the law is on your side. Your rights under Article 20, the written demand, and how to file at the Rental Dispute Centre.',
    category: 'Tenancy',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '7 min read',
  },
  {
    slug: 'car-rental-deposit-refund-time',
    title: 'How Long Does a Car Rental Deposit Refund Take in Dubai?',
    description:
      'Car rental deposit refund times in Dubai explained: the typical 20–30 day window, why companies wait for fines and Salik to post, and how to get your money back faster.',
    category: 'Car Rental',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '6 min read',
  },
  {
    slug: 'car-rental-hold-on-credit-card',
    title: 'Car Rental Holds on Your Credit Card: How They Work in the UAE',
    description:
      'What a car rental hold on your credit card actually is, how much UAE companies block, how long the hold lasts, and the difference between a hold and a charge.',
    category: 'Car Rental',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '6 min read',
  },
  {
    slug: 'salik-fines-car-rental-deposit',
    title: 'Salik, Fines, and Your Car Rental Deposit: What Gets Deducted?',
    description:
      'How Salik tolls and traffic fines are deducted from your car rental deposit in Dubai — current Salik rates, why fines post late, and what a fair deduction looks like.',
    category: 'Car Rental',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '6 min read',
  },
  {
    slug: 'why-car-rental-companies-take-deposit',
    title: 'Why Do Car Rental Companies Take a Deposit?',
    description:
      'The real reasons car rental companies take a security deposit — fines, tolls, damage, and fuel — what the deposit does and does not cover, and how modern rentals do it without blocking cash.',
    category: 'Car Rental',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '6 min read',
  },
  {
    slug: 'car-rental-deposit-not-refunded',
    title: "Car Rental Deposit Not Refunded? Here's What to Do in the UAE",
    description:
      'If your car rental deposit was not refunded in the UAE, follow this checklist: confirm the release window, request an itemised statement, dispute unfair deductions, and escalate.',
    category: 'Car Rental',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '7 min read',
  },
  {
    slug: 'how-to-collect-security-deposits-from-customers',
    title: 'How to Collect Security Deposits From Customers (Without Losing Them)',
    seoTitle: 'How to Collect Security Deposits From Customers',
    description:
      'A practical playbook for collecting security deposits from customers: choosing hold vs charge, setting the amount, communicating clearly, and releasing fast enough to keep bookings.',
    category: 'Business',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '7 min read',
  },
  {
    slug: 'pre-authorization-vs-charge',
    title: "Pre-Authorization vs Charge: What's the Difference?",
    description:
      'Pre-authorization vs charge, explained simply: what each does to the customer’s money, when holds expire, which one suits security deposits, and why the difference decides refund speed.',
    category: 'Business',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '6 min read',
  },
  {
    slug: 'how-long-can-a-business-hold-a-security-deposit',
    title: 'How Long Can a Business Hold a Security Deposit?',
    description:
      'How long a business can hold a security deposit in the UAE: what contracts should say, how long card authorizations really last, and how renewal keeps holds alive for long agreements.',
    category: 'Business',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '6 min read',
  },
  {
    slug: 'cheque-guarantee-alternative-uae',
    title: 'The End of the Security Cheque? Alternatives for UAE Businesses',
    description:
      'Since the UAE decriminalized bounced cheques in 2022, the security cheque is a weak guarantee. The real alternatives for UAE businesses: card holds, upfront deposits, and waivers compared.',
    category: 'Business',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '7 min read',
  },
  {
    slug: 'deposit-insurance-vs-card-hold',
    title: 'Deposit Insurance vs Card Hold: Which Protects Your Business Better?',
    seoTitle: 'Deposit Insurance vs Card Hold: Which Is Better?',
    description:
      'Deposit insurance and waiver products vs a card authorization hold: cost, coverage, claims friction, and customer experience compared for UAE rental and service businesses.',
    category: 'Business',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '6 min read',
  },
  {
    slug: 'holiday-home-damage-deposit-dubai',
    title: 'Holiday Home Damage Deposits in Dubai: What Guests and Hosts Should Know',
    seoTitle: 'Holiday Home Damage Deposits in Dubai',
    description:
      'How damage deposits work for Dubai holiday homes — there is no legislated amount, so the operator sets it. What is fair, how it is held, and how guests get it back.',
    category: 'Holiday Homes',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '6 min read',
  },
  {
    slug: 'airbnb-security-deposit-uae',
    title: 'Airbnb Security Deposits in the UAE: How They Actually Work',
    description:
      'Airbnb does not charge guests an upfront security deposit — it authorizes and relies on AirCover. What that means for UAE hosts and guests, and why many hosts add their own hold.',
    category: 'Holiday Homes',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '6 min read',
  },
  {
    slug: 'dtcm-holiday-home-rules-dubai',
    title: 'DTCM Holiday Home Rules in Dubai: The DET Licence Guide',
    description:
      'What DTCM (now DET) requires to run a holiday home in Dubai: the permit, the 90-day booking cap, tourism tax, and where deposits fit — they are operator-set, not regulated.',
    category: 'Holiday Homes',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '7 min read',
  },
  {
    slug: 'guest-damage-protection-short-term-rentals',
    title: 'Guest Damage Protection for Short-Term Rentals: Your Options',
    description:
      'Every way to protect a short-term rental against guest damage — platform coverage, waivers, upfront deposits, and card holds — compared on cost, coverage, and guest experience.',
    category: 'Holiday Homes',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '6 min read',
  },
  {
    slug: 'short-term-rental-deposit-dubai',
    title: 'Short-Term Rental Deposits in Dubai: A Host Guide',
    description:
      'A practical guide to security deposits for Dubai short-term rentals: how much to hold, direct bookings vs platforms, tourism-tax context, and releasing fast to protect your ratings.',
    category: 'Holiday Homes',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '6 min read',
  },
  {
    slug: 'what-is-a-security-deposit',
    title: 'What Is a Security Deposit? A Simple Definition',
    description:
      'A security deposit is money held to cover damage, loss, or non-payment, and returned if none occurs. What it is, how it is held, and how it differs from a payment.',
    category: 'Glossary',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '4 min read',
  },
  {
    slug: 'what-is-a-pre-authorization-hold',
    title: 'What Is a Pre-Authorization Hold?',
    description:
      'A pre-authorization hold reserves money on a card without charging it. What a hold is, how long it lasts, why it differs from a charge, and where it is used.',
    category: 'Glossary',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '4 min read',
  },
  {
    slug: 'what-is-a-damage-deposit',
    title: 'What Is a Damage Deposit?',
    description:
      'A damage deposit is a security deposit taken specifically to cover damage to a rented item or property. What it covers, how it differs from a cleaning fee, and how it is returned.',
    category: 'Glossary',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '4 min read',
  },
  {
    slug: 'security-deposit-vs-advance-payment',
    title: 'Security Deposit vs Advance Payment: What Is the Difference?',
    description:
      'A security deposit is refundable and held against damage; an advance payment is money paid toward what you owe. The difference, and why it matters for refunds and accounting.',
    category: 'Glossary',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '4 min read',
  },
  {
    slug: 'what-is-a-refundable-deposit',
    title: 'What Is a Refundable Deposit?',
    description:
      'A refundable deposit is money returned to you in full if agreed conditions are met. What makes a deposit refundable, what can be deducted, and how to make sure you get it back.',
    category: 'Glossary',
    date: '2026-07-22',
    dateLabel: 'July 22, 2026',
    readingTime: '4 min read',
  },
]
